import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tool, Category, PricingType } from '@/types/database';

interface UseToolsOptions {
  category?: string;
  pricing?: PricingType;
  search?: string;
  featured?: boolean;
}

export function useTools(options: UseToolsOptions = {}) {
  return useQuery({
    queryKey: ['tools', options],
    queryFn: async () => {
      // If filtering by category, we need to join with tool_categories
      if (options.category) {
        // First get category ID
        const { data: categoryData } = await supabase
          .from('categories')
          .select('id')
          .eq('slug', options.category)
          .single();
        
        if (!categoryData) return [];

        // Get tool IDs in this category
        const { data: toolCategoryData } = await supabase
          .from('tool_categories')
          .select('tool_id')
          .eq('category_id', categoryData.id);

        if (!toolCategoryData || toolCategoryData.length === 0) return [];

        const toolIds = toolCategoryData.map(tc => tc.tool_id);

        let query = supabase
          .from('tools')
          .select('*')
          .eq('status', 'approved')
          .in('id', toolIds)
          .order('created_at', { ascending: false });

        if (options.pricing) {
          query = query.eq('pricing', options.pricing);
        }

        if (options.search) {
          const searchTerm = options.search.toLowerCase();
          query = query.or(`name.ilike.%${searchTerm}%,short_description.ilike.%${searchTerm}%,full_description.ilike.%${searchTerm}%`);
        }

        const { data, error } = await query;
        if (error) throw error;
        return (data || []) as Tool[];
      }

      // Standard query without category filter
      let query = supabase
        .from('tools')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (options.featured) {
        query = query.eq('is_featured', true);
      }

      if (options.pricing) {
        query = query.eq('pricing', options.pricing);
      }

      if (options.search) {
        const searchTerm = options.search.toLowerCase();
        query = query.or(`name.ilike.%${searchTerm}%,short_description.ilike.%${searchTerm}%,full_description.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []) as Tool[];
    }
  });
}

export function useTool(slug: string) {
  return useQuery({
    queryKey: ['tool', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      const { data: categoryLinks, error: categoryError } = await supabase
        .from('tool_categories')
        .select('category:categories(*)')
        .eq('tool_id', data.id);
      if (categoryError) throw categoryError;
      return { ...data, categories: (categoryLinks || []).flatMap(link => link.category ? [link.category] : []) } as Tool;
    },
    enabled: !!slug
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) throw error;
      return data as Category[];
    }
  });
}

export function useToolReviews(toolId: string) {
  return useQuery({
    queryKey: ['reviews', toolId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select('*, profiles:user_id(username, avatar_url)')
        .eq('tool_id', toolId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!toolId
  });
}

export function useToolsWithRatings(toolIds: string[]) {
  return useQuery({
    queryKey: ['tools-ratings', toolIds],
    queryFn: async () => {
      if (toolIds.length === 0) return {};
      
      const { data, error } = await supabase
        .from('reviews')
        .select('tool_id, rating')
        .in('tool_id', toolIds);

      if (error) throw error;

      // Aggregate ratings by tool
      const ratings: Record<string, { avg: number; count: number }> = {};
      toolIds.forEach(id => {
        const toolReviews = (data || []).filter(r => r.tool_id === id);
        if (toolReviews.length > 0) {
          const avg = toolReviews.reduce((sum, r) => sum + r.rating, 0) / toolReviews.length;
          ratings[id] = { avg: Math.round(avg * 10) / 10, count: toolReviews.length };
        }
      });
      return ratings;
    },
    enabled: toolIds.length > 0
  });
}

export function useAddReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ toolId, rating, title, content }: { toolId: string; rating: number; title?: string; content?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('reviews')
        .insert({
          tool_id: toolId,
          user_id: user.id,
          rating,
          title,
          content
        });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reviews', variables.toolId] });
    }
  });
}

export function useFavorites() {
  return useQuery({
    queryKey: ['favorites'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('favorites')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return data;
    }
  });
}

export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (toolId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: existing } = await supabase
        .from('favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('tool_id', toolId)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('favorites')
          .insert({ user_id: user.id, tool_id: toolId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    }
  });
}
